import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, doc, setDoc, orderBy, limit } from 'firebase/firestore';
import { db, auth, setDocWithQueue, handleFirestoreError, OperationType } from '../services/firebase';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';

interface Notification {
  id: string;
  timestamp: number;
  type: string;
  title: string;
  message: string;
  read: boolean;
}

export const GamificationNotifications: React.FC<{uid?: string}> = ({ uid }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    if (!uid) return;
    

    const notificationsRef = collection(db, 'users', uid, 'notifications');
    // We get the most recent unread notifications
    const q = query(
      notificationsRef, 
      orderBy('timestamp', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newNotifs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Notification))
        .filter(notif => !notif.read)
        .slice(0, 3);

      // Bridge Firestore notifications to Native OS-level notifications (PWA standalone required for iOS Safari)
      if (newNotifs.length > 0 && 'Notification' in window && Notification.permission === 'granted') {
        newNotifs.forEach(notif => {
          const shownKey = `native_notif_${notif.id}`;
          if (!localStorage.getItem(shownKey)) {
            localStorage.setItem(shownKey, 'true');
            
            const title = notif.title || 'MAIN Nutrition';
            const body = notif.message || 'Check out your progress, bro!';
            
            if (navigator.serviceWorker && navigator.serviceWorker.controller) {
              navigator.serviceWorker.ready.then(reg => {
                reg.showNotification(title, {
                  body: body,
                  icon: '/icon-512.png',
                  badge: '/icon.svg',
                  vibrate: [100, 50, 100],
                } as any);
              }).catch(() => {
                // Fallback to standalone native Notification if SW ready fails
                new Notification(title, { body: body, icon: '/icon-512.png' });
              });
            } else {
              new Notification(title, { body: body, icon: '/icon-512.png' });
            }
          }
        });
      }

      setNotifications(newNotifs);
    }, (error) => {
      console.error("Gamification notifications subscription failed:", error);
      handleFirestoreError(error, OperationType.GET, `users/${uid}/notifications`);
    });

    return () => unsubscribe();
  }, [uid]);

  const markAsRead = async (id: string) => {
    if (!uid) return;
    
    try {
      const notifRef = doc(db, 'users', uid, 'notifications', id);
      await setDocWithQueue(notifRef, { read: true }, { merge: true });
    } catch (err) {
      console.error("Error marking notification as read", err);
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-20 sm:bottom-24 left-4 right-4 sm:left-auto sm:right-4 z-50 flex flex-col gap-3 max-w-sm w-auto pointer-events-none">
      <AnimatePresence>
        {notifications.map((notif) => (
          <motion.div
            key={notif.id}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className="pointer-events-auto bg-zinc-900/95 dark:bg-zinc-900/95 backdrop-blur-md border-l-4 border-[#10B981] p-3.5 sm:p-4 rounded-xl shadow-xl border border-zinc-800 relative"
          >
            <button 
              onClick={() => markAsRead(notif.id)}
              className="absolute top-2.5 right-2.5 text-zinc-400 hover:text-white transition-colors p-1"
            >
              <X size={16} />
            </button>
            <h4 className="font-mono text-[#10B981] uppercase tracking-wider font-bold text-xs sm:text-sm pr-6">
              {notif.title}
            </h4>
            <p className="text-zinc-200 font-sans mt-1 text-xs sm:text-sm leading-relaxed">
              {notif.message}
            </p>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
